import { useState, useEffect, useMemo } from 'react';

import type { FC, ReactNode } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Calendar, CheckCircle, Clock, LayoutDashboard } from 'lucide-react';

import { Row, Col, Select, message } from 'antd';

import type { ColumnsType } from 'antd/es/table';

import { brandApi, dashboardApi, templateApi } from '../api/services';

import ProjectMonitorDetail from './ProjectMonitorDetail';

import type {

  BrandVO,

  DashboardProjectProgressGridVO,

  DashboardProjectProgressRowVO,

  MilestoneTemplateVO,

} from '../api/types';

import dayjs from 'dayjs';
import { projectStatusLabel, projectStatusOptions } from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import { projectStatusBadgeVariant } from '../utils/ipcStatusVisual';

import DashboardReport from './DashboardReport';

import {

  IpcFilterBar,

  IpcNodeStatus,


  IpcModuleTabs,

  IpcStatCard,

  type IpcStatCardTone,

  IpcStatusBadge,

  IpcTable,

} from '../components/ui';

import { ProjectCodeDisplay, SpecSkuDisplay } from '../components/ProjectCodeDisplay';

import './Dashboard.css';

import { parseNodeStatus } from '../utils/nodeStatus';
import { normalizeNodeVisualStatus } from '../utils/ipcStatusVisual';



/** 兼容后端 snake_case / PascalCase / camelCase */

function pickStatField(obj: Record<string, unknown>, keys: string[]): number {

  for (const k of keys) {

    const v = obj[k];

    if (v == null || v === '') continue;

    const n = Number(v);

    if (!Number.isNaN(n)) return n;

  }

  return 0;

}



function normalizeProgressGrid(raw: unknown): DashboardProjectProgressGridVO {

  const o = raw as Record<string, unknown>;

  const cols = (o?.milestone_columns ?? o?.MilestoneColumns ?? []) as Record<string, unknown>[];

  const rows = (o?.rows ?? o?.Rows ?? []) as Record<string, unknown>[];

  const mappedRows = rows.map(r => ({

    project_id: Number(r.project_id ?? r.ProjectId ?? 0),

    project_code: String(r.project_code ?? r.ProjectCode ?? ''),

    ipd_project_codes: (() => {
      const rawCodes = r.ipd_project_codes ?? r.IpdProjectCodes;
      if (!Array.isArray(rawCodes)) return [] as string[];
      return rawCodes.map(c => String(c ?? '').trim()).filter(Boolean);
    })(),

    project_name: String(r.project_name ?? r.ProjectName ?? ''),

    brand: String(r.brand ?? r.Brand ?? ''),

    project_status: String(r.project_status ?? r.ProjectStatus ?? ''),

    project_status_label: (() => {

      const v = r.project_status_label ?? r.ProjectStatusLabel;

      return v != null && String(v).trim() !== '' ? String(v).trim() : '';

    })(),

    template_id: (r.template_id ?? r.TemplateId) != null ? Number(r.template_id ?? r.TemplateId) : null,

    template_name: String(r.template_name ?? r.TemplateName ?? ''),

    spec_schedule_id: Number(r.spec_schedule_id ?? r.SpecScheduleId ?? 0),

    spec_name: String(r.spec_name ?? r.SpecName ?? ''),

    sku_codes: String(r.sku_codes ?? r.SkuCodes ?? ''),

    in_progress_milestones: String(r.in_progress_milestones ?? r.InProgressMilestones ?? ''),

    milestone_statuses: (r.milestone_statuses ?? r.MilestoneStatuses ?? []) as string[],

    update_time: String(r.update_time ?? r.UpdateTime ?? ''),

  }));

  const sortedRows = [...mappedRows].sort((a, b) => {

    const diff = dayjs(b.update_time).valueOf() - dayjs(a.update_time).valueOf();

    if (!Number.isNaN(diff) && diff !== 0) return diff;

    if (a.project_id !== b.project_id) return b.project_id - a.project_id;

    return a.spec_schedule_id - b.spec_schedule_id;

  });

  return {

    milestone_columns: cols.map(c => ({

      milestone_id: Number(c.milestone_id ?? c.MilestoneId ?? 0),

      milestone_name: String(c.milestone_name ?? c.MilestoneName ?? ''),

    })),

    rows: sortedRows,

  };

}



function renderMilestoneStatusCell(status: string | undefined) {
  const visual = normalizeNodeVisualStatus(status);
  if (visual === 'terminated') {
    return <IpcNodeStatus progress="terminated" compact showLabel />;
  }
  const { progress, time } = parseNodeStatus(status);
  return <IpcNodeStatus progress={progress} time={time} compact showLabel />;
}

function projectStatusDisplayText(status: string, label?: string) {
  const t = label?.trim();
  if (t) return t;
  return projectStatusLabel(status);
}



function renderProjectStatusTag(status: string, label?: string) {

  const text = projectStatusDisplayText(status, label);

  return <IpcStatusBadge variant={projectStatusBadgeVariant(status)}>{text}</IpcStatusBadge>;

}



const DASHBOARD_TABS = ['overview', 'report', 'monitor'] as const;
type DashboardTabKey = (typeof DASHBOARD_TABS)[number];

function isDashboardTabKey(value: string | undefined): value is DashboardTabKey {
  return value != null && (DASHBOARD_TABS as readonly string[]).includes(value);
}

type OverviewFilters = {
  brand: string;
  status: string;
  templateId: number | null;
  search: string;
};

const DEFAULT_OVERVIEW_FILTERS: OverviewFilters = {
  brand: 'all',
  status: 'all',
  templateId: null,
  search: '',
};

const Dashboard: FC = () => {

  useIpcStatusEnums();

  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const activeTab: DashboardTabKey = isDashboardTabKey(tabParam) ? tabParam : 'overview';

  useEffect(() => {
    if (!isDashboardTabKey(tabParam)) {
      navigate('/dashboard/overview', { replace: true });
    }
  }, [tabParam, navigate]);

  const [stats, setStats] = useState({

    total: 0,

    completed: 0,

    inProgress: 0,

    planning: 0,

  });

  const [brandOptions, setBrandOptions] = useState<Array<{ value: string; label: string }>>([]);

  const [templateOptions, setTemplateOptions] = useState<Array<{ value: number; label: string }>>([]);

  const {
    draft: overviewDraft,
    patchDraft: patchOverviewDraft,
    applied: overviewApplied,
    reset: resetOverviewQuery,
    patchApplied: patchOverviewApplied,
    applyIfChanged: applyOverviewIfChanged,
    patchAndApply: patchAndApplyOverview,
    page: progressPageNum,
    pageSize: progressPageSize,
    setPagination: setProgressPagination,
  } = useListViewQuery<OverviewFilters>({
    listViewKey: LIST_VIEW_KEYS.dashboardOverview,
    defaultFilters: DEFAULT_OVERVIEW_FILTERS,
    emptyFilters: DEFAULT_OVERVIEW_FILTERS,
  });

  const filterBrand = overviewApplied.brand;
  const filterStatus = overviewApplied.status;
  const filterTemplateId = overviewApplied.templateId ?? undefined;
  const filterSearch = overviewApplied.search;
  const draftBrand = overviewDraft.brand;
  const draftStatus = overviewDraft.status;
  const draftTemplateId = overviewDraft.templateId ?? undefined;
  const draftSearch = overviewDraft.search;

  const [grid, setGrid] = useState<DashboardProjectProgressGridVO>({ milestone_columns: [], rows: [] });

  const [gridLoading, setGridLoading] = useState(false);



  const brandLabelMap = useMemo(() => {

    const m: Record<string, string> = {};

    brandOptions.forEach(b => { m[b.value] = b.label; });

    return m;

  }, [brandOptions]);



  useEffect(() => {

    (async () => {

      try {

        const brands = await brandApi.getList();

        setBrandOptions(brands.map((b: BrandVO) => ({ value: b.code, label: b.name })));

      } catch {

        // ignore

      }

    })();

  }, []);



  useEffect(() => {

    (async () => {

      try {

        const res = await templateApi.getList({ pageNum: 1, pageSize: 200 });

        setTemplateOptions((res.list ?? []).map((t: MilestoneTemplateVO) => ({ value: t.id, label: t.name ?? '' })));

      } catch {

        // ignore

      }

    })();

  }, []);



  useEffect(() => {

    (async () => {

      try {

        const statsRes = await dashboardApi.getStats({ brand: filterBrand === 'all' ? undefined : filterBrand, status: filterStatus === 'all' ? undefined : filterStatus });

        setStats({

          total: pickStatField(statsRes as unknown as Record<string, unknown>, ['totalProjects', 'TotalProjects']),

          completed: pickStatField(statsRes as unknown as Record<string, unknown>, ['completedProjects', 'CompletedProjects']),

          inProgress: pickStatField(statsRes as unknown as Record<string, unknown>, ['inProgressProjects', 'InProgressProjects']),

          planning: pickStatField(statsRes as unknown as Record<string, unknown>, ['planningProjects', 'PlanningProjects']),

        });

      } catch {

        // ignore

      }

    })();

  }, [filterBrand, filterStatus, filterTemplateId]);



  useEffect(() => {

    setGridLoading(true);

    (async () => {

      try {

        const raw = await dashboardApi.getProjectProgressList({

          brand: filterBrand === 'all' ? undefined : filterBrand,

          status: filterStatus === 'all' ? undefined : filterStatus,

          templateId: filterTemplateId,

        });

        setGrid(normalizeProgressGrid(raw));

      } catch {

        message.error('加载项目进度失败');

      } finally {

        setGridLoading(false);

      }

    })();

  }, [filterBrand, filterStatus, filterTemplateId]);



  const progressFilteredRows = useMemo(() => {

    const keyword = filterSearch.trim().toLowerCase();

    if (!keyword) return grid.rows;

    return grid.rows.filter(row =>

      [
        row.project_name,
        row.project_code,
        ...(row.ipd_project_codes ?? []),
        row.spec_name,
        row.sku_codes,
        row.template_name,
      ]

        .some(value => String(value ?? '').toLowerCase().includes(keyword)),

    );

  }, [grid.rows, filterSearch]);



  const progressPagedRows = useMemo(() => {

    const start = (progressPageNum - 1) * progressPageSize;

    return progressFilteredRows.slice(start, start + progressPageSize);

  }, [progressFilteredRows, progressPageNum, progressPageSize]);



  useEffect(() => {
    if (progressFilteredRows.length === 0) return;
    const maxPage = Math.max(1, Math.ceil(progressFilteredRows.length / progressPageSize));
    if (progressPageNum > maxPage) setProgressPagination(maxPage, progressPageSize);
  }, [progressFilteredRows.length, progressPageSize, progressPageNum, setProgressPagination]);



  const columns = useMemo((): ColumnsType<DashboardProjectProgressRowVO> => {

    const openProject = (projectId: number) => {

      if (projectId > 0) navigate(`/schedule/${projectId}`);

    };



    const fixed: ColumnsType<DashboardProjectProgressRowVO> = [

      {

        title: '品牌',

        dataIndex: 'brand',

        key: 'brand',

        width: 120,

        ellipsis: true,

        fixed: 'start' as const,

        className: 'dashboard-project-fixed-cell dashboard-project-brand-cell',

        onCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-brand-cell' }),

        onHeaderCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-brand-cell' }),

        render: (_: string, record: DashboardProjectProgressRowVO) =>

          brandLabelMap[record.brand] ?? record.brand ?? '—',

      },

      {

        title: '项目号',

        dataIndex: 'project_code',

        key: 'project_code',

        width: 200,

        ellipsis: true,

        fixed: 'start' as const,

        className: 'dashboard-project-fixed-cell dashboard-project-code-cell',

        onCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-code-cell' }),

        onHeaderCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-code-cell' }),

        render: (_code: string, record: DashboardProjectProgressRowVO) => (
          <ProjectCodeDisplay
            codes={record.ipd_project_codes}
            fallback={record.project_code}
            link
            onClick={() => openProject(record.project_id)}
          />
        ),

      },

      {

        title: '项目名称',

        dataIndex: 'project_name',

        key: 'project_name',

        width: 180,

        fixed: 'start' as const,

        ellipsis: true,

        className: 'dashboard-project-fixed-cell dashboard-project-name-cell',

        onCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-name-cell' }),

        onHeaderCell: () => ({ className: 'dashboard-project-fixed-cell dashboard-project-name-cell' }),

        render: (name: string, record: DashboardProjectProgressRowVO) => {

          const text = name?.trim();

          if (!text) return '—';

          return (

            <a className="ipc-link dashboard-project-link" onClick={() => openProject(record.project_id)}>

              {text}

            </a>

          );

        },

      },

      {

        title: '模板',

        dataIndex: 'template_name',

        key: 'template_name',

        width: 160,

        ellipsis: true,

        render: (t: string) => t?.trim() || '—',

      },

      {

        title: '规格',

        dataIndex: 'spec_name',

        key: 'spec_name',

        width: 140,

        ellipsis: true,

        render: (_: string, record: DashboardProjectProgressRowVO) => (
          <SpecSkuDisplay title={record.spec_name} skuCodes={record.sku_codes} />
        ),

      },

      {

        title: '项目状态',

        dataIndex: 'project_status',

        key: 'project_status',

        width: 100,

        render: (_: string, record: DashboardProjectProgressRowVO) =>

          renderProjectStatusTag(record.project_status, record.project_status_label),

      },

      {

        title: '进行中的节点',

        dataIndex: 'in_progress_milestones',

        key: 'in_progress_milestones',

        width: 220,

        ellipsis: true,

        render: (t: string) => {

          const text = t?.trim();

          if (!text) return <span className="dashboard-ms dashboard-ms--empty">—</span>;

          return <span className="ipc-text-warning">{text}</span>;

        },

      },

    ];



    const dyn: ColumnsType<DashboardProjectProgressRowVO> = (grid.milestone_columns ?? []).map((col, idx) => ({

      title: col.milestone_name,

      key: `milestone-${col.milestone_id}`,

      width: 112,

      align: 'center' as const,

      render: (_: unknown, record: DashboardProjectProgressRowVO) => {

        const st = record.milestone_statuses?.[idx];

        return renderMilestoneStatusCell(st);

      },

    }));



    return [...fixed, ...dyn];

  }, [grid.milestone_columns, brandLabelMap, navigate]);



  const statShare = (n: number) =>

    stats.total > 0 ? `占比 ${((n / stats.total) * 100).toFixed(1)}%` : '占比 —';



  const statCards: Array<{

    value: number;

    label: string;

    meta: string;

    icon: ReactNode;

    tone: IpcStatCardTone;

  }> = [

    {

      value: stats.total,

      label: '项目总数',

      meta: stats.total > 0 ? '当前筛选范围' : '暂无项目',

      icon: <LayoutDashboard size={20} />,

      tone: 'default',

    },

    {

      value: stats.completed,

      label: projectStatusLabel('completed'),

      meta: statShare(stats.completed),

      icon: <CheckCircle size={20} />,

      tone: 'success',

    },

    {

      value: stats.inProgress,

      label: projectStatusLabel('in_progress'),

      meta: statShare(stats.inProgress),

      icon: <Clock size={20} />,

      tone: 'primary',

    },

    {

      value: stats.planning,

      label: projectStatusLabel('planning'),

      meta: statShare(stats.planning),

      icon: <Calendar size={20} />,

      tone: 'muted',

    },

  ];



  const applyProgressFilters = () => {
    applyOverviewIfChanged({
      ...overviewDraft,
      search: overviewDraft.search.trim(),
    });
  };



  /** 重置 / 清除全部：默认即「全部」，二者一致但回调分离 */
  const clearAllProgressFilters = () => {
    resetOverviewQuery();
  };

  const resetProgressFiltersToDefault = clearAllProgressFilters;



  const templateLabel = templateOptions.find(item => item.value === filterTemplateId)?.label;

  const brandLabel = brandOptions.find(item => item.value === filterBrand)?.label;

  const appliedFilters = [

    filterSearch.trim() ? { key: 'search', label: `关键词: ${filterSearch.trim()}` } : null,

    filterTemplateId ? { key: 'template', label: `模板: ${templateLabel ?? filterTemplateId}` } : null,

    filterBrand !== 'all' ? { key: 'brand', label: `品牌: ${brandLabel ?? filterBrand}` } : null,

    filterStatus !== 'all'

      ? {

          key: 'status',

          label: `状态: ${projectStatusLabel(filterStatus)}`,

        }

      : null,

  ].filter((item): item is { key: string; label: string } => item !== null);



  const removeAppliedFilter = (key: string) => {
    if (key === 'search') patchOverviewApplied({ search: '' });
    else if (key === 'template') patchOverviewApplied({ templateId: null });
    else if (key === 'brand') patchOverviewApplied({ brand: 'all' });
    else if (key === 'status') patchOverviewApplied({ status: 'all' });
  };



  const progressOverview = (

    <div className="ipc-section-stack">

      <IpcFilterBar

        searchValue={draftSearch}

        onSearchChange={value => patchOverviewDraft({ search: value })}

        onSearchSubmit={applyProgressFilters}

        onReset={resetProgressFiltersToDefault}

        chipItems={appliedFilters}

        onRemoveChip={removeAppliedFilter}

        onClearChips={clearAllProgressFilters}

      >

        <Select

          placeholder="全部模板"

          allowClear

          value={draftTemplateId}

          onChange={(v: number | null) => patchAndApplyOverview({ templateId: v ?? null })}

          options={templateOptions}

          fieldNames={{ label: 'label', value: 'value' }}

          className="ipc-filter-bar__field"

        />

        <Select

          value={draftBrand}

          onChange={v => patchAndApplyOverview({ brand: v ?? 'all' })}

          options={[{ value: 'all', label: '全部品牌' }, ...brandOptions]}

          className="ipc-filter-bar__field"

        />

        <Select

          value={draftStatus}

          onChange={v => patchAndApplyOverview({ status: v ?? 'all' })}

          options={[

            { value: 'all', label: '全部状态' },

            ...projectStatusOptions(),

          ]}

          className="ipc-filter-bar__field"

        />

      </IpcFilterBar>

      <IpcTable<DashboardProjectProgressRowVO>
        listViewKey={LIST_VIEW_KEYS.dashboardOverview}
        panelClassName="dashboard-table-region"
        scrollClassName="dashboard-table-scroll"
        /* preline：里程碑列名/单元格可换行撑开；单行仍默认表头/行高 40px */
        className="ipc-table-preline"
        tableLayout="fixed"
        columns={columns}
        dataSource={progressPagedRows}
        loading={gridLoading}
        rowKey={r => `${r.project_id}-${r.spec_schedule_id}`}
        autoHeight
        autoHeightDeps={[
          progressPagedRows.length,
          progressPageNum,
          progressPageSize,
          gridLoading,
          grid.milestone_columns.length,
        ]}
        scroll={{ x: 1080 + (grid.milestone_columns?.length ?? 0) * 96 }}
        pagination={{
          className: 'dashboard-table-pagination',
          current: progressPageNum,
          pageSize: progressPageSize,
          total: progressFilteredRows.length,
          onChange: (page, size) => {
            setProgressPagination(page, size);
          },
        }}
        resizable
      />

      {/* <div className="dashboard-overview-note">
        <span>说明：未选模板时展示全部里程碑节点；选择模板后仅展示使用该模板创建的项目及模板内节点。</span>
        <span><IpcNodeStatus progress="completed" compact showLabel /></span>
        <span><IpcNodeStatus progress="in_progress" compact showLabel /></span>
        <span><IpcNodeStatus progress="pending" compact showLabel /></span>
        <span><IpcNodeStatus progress="in_progress" time="delayed" compact showLabel title="延期" /></span>
      </div> */}

    </div>

  );



  return (

    <div className="dashboard dashboard-page ipc-page">
      <IpcModuleTabs

        activeKey={activeTab}

        onChange={key => {
          if (isDashboardTabKey(key)) {
            navigate(`/dashboard/${key}`);
          }
        }}

        destroyOnHidden
        items={[

          {

            key: 'overview',

            label: '项目概览',

            children: (

              <div className="ipc-page-stack">

                <Row gutter={[16, 16]} className="ipc-stat-grid">

                  {statCards.map((item, i) => (

                    <Col xs={24} sm={12} md={6} key={i}>

                      <IpcStatCard

                        label={item.label}

                        value={item.value}

                        meta={item.meta}

                        icon={item.icon}

                        tone={item.tone}

                      />

                    </Col>

                  ))}

                </Row>

                {progressOverview}

              </div>

            ),

          },

          {

            key: 'report',

            label: '项目汇报',

            children: <DashboardReport />,

          },

          {

            key: 'monitor',

            label: '项目监控',

            children: <ProjectMonitorDetail />,

          },

        ]}

      />

    </div>

  );

};



export default Dashboard;


